export const ATTENTION_POLYGON_LAYER = "Attention Polygon";
export const ATTENTION_WELL_LAYER = "Attention Well";
/**
 * Point counterpart of ATTENTION_WELL_LAYER, sharing its source.
 *
 * A mapbox `line` layer draws LineString geometry and ignores Point, so the
 * highlight was invisible for vertical wells however correctly they resolved.
 * Both layers read the same source and each paints the geometry it can, so
 * nothing has to sort features by kind before handing them over.
 */
export const ATTENTION_WELL_POINT_LAYER = "Attention Well Vert";
// A jumped-to well, drawn apart from chart hover so only a jump glows: a black
// line or dot, each over a red glow layer that reads the same source.
export const ATTENTION_WELL_JUMP_LAYER = "Attention Well Jump";
export const ATTENTION_WELL_JUMP_GLOW_LAYER = "Attention Well Jump Glow";
export const ATTENTION_WELL_DOT_LAYER = "Attention Well Dot";
export const ATTENTION_WELL_DOT_GLOW_LAYER = "Attention Well Dot Glow";

// The radius stops the vertical well glyph itself draws at, by zoom.
/**
 * The radius a vertical well glyph draws at, by zoom, for a given
 * `wellDotThickness` display setting.
 *
 * Shared so the glyph, the runtime restyle and the attention marker that sits
 * on top of it cannot drift: the marker was pinned to the thickness-0 ramp and
 * disappeared behind the glyph as soon as a user raised the dot size.
 */
export const vertWellCircleRadius = (dotThickness = 0) => [
  "interpolate",
  ["linear"],
  ["zoom"],
  6,
  2,
  15,
  4 + dotThickness * 2,
  22,
  8 + dotThickness * 4
];
// The glyph's radius plus 1px, so the dot covers the glyph and stands out from
// it at every dot size. The zoom-6 stop is dropped so the dot holds its zoom-15
// size when zoomed out (mapbox clamps below the first stop) rather than
// shrinking out of sight with the glyph.
export const highlightDotRadius = (dotThickness = 0) => {
  const [interpolate, curve, input, , , ...stops] = vertWellCircleRadius(dotThickness);
  return [
    interpolate,
    curve,
    input,
    ...stops.map((value, index) => (index % 2 === 1 ? (value as number) + 1 : value))
  ];
};
// A highlighted well is black over a translucent red glow spread evenly around
// it. The glow fades out across most of its width, so it reads as a soft halo
// rather than a second, wider line.
export const HIGHLIGHT_LINE_LAYOUT = {
  "line-cap": "round",
  "line-join": "round"
} as const;
export const highlightLineGlowPaint = (coreWidth: number) => ({
  "line-color": "red",
  "line-opacity": 0.7,
  "line-width": coreWidth * 4,
  "line-blur": coreWidth * 1.5
});
// Three times the dot, so a vertical's glow spreads about as far as a
// horizontal's and follows the dot size setting with it.
export const highlightGlowRadius = (dotThickness = 0) => {
  const [interpolate, curve, input, ...stops] = highlightDotRadius(dotThickness);
  return [
    interpolate,
    curve,
    input,
    ...stops.map((value, index) => (index % 2 === 1 ? (value as number) * 3 : value))
  ];
};
export const HIGHLIGHT_GLOW_PAINT = {
  "circle-color": "red",
  "circle-opacity": 0.7,
  "circle-blur": 0.8
};
export const BASE_WELL_LAYER = "Base Well";
export const BASE_WELL_LAYER_PARENT = "Base Layers";
export const BUBBLE_LAYER = "Bubble";
export const COVERAGE_LAYER = "Well Coverage";
export const COMPLETION_FRAC_LAYER = "Frac Line";
export const COMPLETION_PERF_LAYER = "Perforation Points";
export const EXPLOITED_LAYER = "Exploited Area";
export const HIGHLIGHTED_WELL_LAYER = "Highlighted Well";
export const HIGHLIGHTED_WELL_POINT_LAYER = "Highlighted Well Point";
export const IPDB_LAYER = "Geo Model";
export const IPDB_CONTROL_WELLS_LAYER = "Geo Model Controls Wells";

// Project Layer Types for IPDB
export const IPDB_LAYER_TYPE = "Geo Model";
export const IPDB_CONTROL_WELLS_LAYER_TYPE = "Geo Model Controls";

export const LSD_LABEL_LAYER = "LSD Label";
export const LSD_LAYER = "LSD";
export const SECTION_LABEL_LAYER = "Section Label";
export const SECTION_LAYER = "Section";
// The single selected well: a black line or dot, each over a red glow layer
// that reads the same source.
export const SELECTED_SINGLE_WELL_LAYER = "Selected Single Well";
export const SELECTED_SINGLE_WELL_GLOW_LAYER = "Selected Single Well Glow";
export const SELECTED_SINGLE_WELL_POINT_LAYER = "Selected Single Well Dot";
export const SELECTED_SINGLE_WELL_POINT_GLOW_LAYER = "Selected Single Well Dot Glow";
export const SELECTED_WELL_LAYER = "Selected Well";
export const SELECTED_WELL_POINT_LAYER = "Selected Well Point";
export const POLYGON_BINS_LAYERS = "Polygon Bins";
export const GEO_MODEL_LAYERS = "Geo Model Layers";
export const POLYGON_BINS_MY_FIELDS_LAYERS = "My Fields";
export const POLYGON_BINS_ORG_FIELDS_LAYERS = "Organization Fields";
export const POLYGON_BINS_PROJECT_FIELDS_LAYERS = "Project Fields";
export const SHAPEFILE_DETAILS_LABEL_LAYER = "Shapefile & GeoTIFF Details Label";
export const SHAPEFILE_NAME_LABELS_LAYER = "Shapefile & GeoTIFF Name Label";
export const SYNC_WELL_LAYER = "Sync Well";
export const TOWNSHIP_LABEL_LAYER = "Township Label";
export const TOWNSHIP_LAYER = "Township";
export const TYPE_LOG_LAYER = "Type Log";
export const TYPE_LOG_VERTICAL_LAYER = "Type Log Vertical";
export const NTS_BLOCK_LABEL_LAYER = "NTS Block Label";
export const UNEXPLOITED_LAYER = "Unexploited Area";
export const WELL_LABEL_LAYER = "Wells Label";
export const WELL_LAYER = "Wells";
export const STICK_LAYER = "Sticks";

// US land grid layers
export const COUNTY_LAYER = "County";
export const COUNTY_LABEL_LAYER = "County Label";
export const US_TOWNSHIP_LAYER = "US Township";
export const US_TOWNSHIP_LABEL_LAYER = "US Township Label";
export const US_SECTION_LAYER = "US Section";
export const US_SECTION_LABEL_LAYER = "US Section Label";
export const TX_BLOCK_LAYER = "Texas Block";
export const TX_BLOCK_LABEL_LAYER = "Texas Block Label";
export const TX_SECTION_LAYER = "Texas Section";
export const TX_SECTION_LABEL_LAYER = "Texas Section Label";
export const TX_ABSTRACT_LAYER = "Texas Abstract";
export const TX_ABSTRACT_LABEL_LAYER = "Texas Abstract Label";

/**
 * Text size each US/Texas grid label layer is created with in getMapLayers.
 * Every one of these layers has its own size setting; this record is both the
 * membership test for "is this a sizeable grid label layer" and the fallback
 * the settings stepper and the map use until the user picks a size. Keep the
 * values in step with the layer definitions in getMapLayers.ts.
 */
export const GRID_LABEL_DEFAULT_SIZES: Readonly<Record<string, number>> = {
  [COUNTY_LABEL_LAYER]: 12,
  [US_TOWNSHIP_LABEL_LAYER]: 9,
  [US_SECTION_LABEL_LAYER]: 12,
  [TX_BLOCK_LABEL_LAYER]: 9,
  [TX_SECTION_LABEL_LAYER]: 12,
  [TX_ABSTRACT_LABEL_LAYER]: 12
};

export const FACILITY_LAYER_ROOT = "Facility Layers";
export const FACILITY_LABEL_LAYER = "Facility Label";
export const FACILITY_LAYER = "Facility";
export const SPIDER_FACILITY_LAYER = "spider-facility-layer";
export const SPIDER_FACILITY_SOURCE = "spider-facility-source";

export const RIG_FRAC_LAYER_ROOT = "Rig & Frac Layers";
export const RIG_LAYER = "Rigs";
export const FRAC_LAYER = "Frac Crews";
export const RIG_FRAC_LAYER_TYPE = "Rig Frac";
// Single source of truth for the rig-vs-frac type split (EVA-63). Reused by both
// getMapLayers.ts (baked into layer.filter) and useRigFracLayerFilter (re-applied
// on selection change / style reload) so the two can never drift — dropping or
// replacing this clause would let rigs/frac crews leak onto the wrong layer (same
// bug class as the EVA-6899 stick-leak regression).
export const RIG_TYPE_CLAUSE = ["==", ["get", "type"], "Rig"];
export const FRAC_TYPE_CLAUSE = ["==", ["get", "type"], "Frac"];
// Static icon-color defaults for the rig/frac layers: the layer-init default,
// the None-state color, and the fallback branch of the color-by match
// expression. Shared by getMapLayers and the color-by adapter so the two never
// drift apart.
export const RIG_COLOR = "#E11D48";
export const FRAC_COLOR = "#2563EB";
export const WELL_LAYER_POINT = "Wells Vert";

/**
 * Every layer that paints a well: horizontals, verticals, and Pad Generator
 * sticks. Mapbox honors layer filters, and `WELL_LAYER` is filtered to
 * `group != "stick"` while `STICK_LAYER` carries the complement — so a
 * `queryRenderedFeatures` that means "wells" and lists fewer than all three
 * silently reads the missing kind as absent.
 */
export const WELL_PAINT_LAYERS = [WELL_LAYER, WELL_LAYER_POINT, STICK_LAYER] as const;

// Lower than the charts' 0.5: overlapping wells stack alpha, so 0.5 barely dims dense areas.
export const MAP_BLUR_OPACITY = 0.4;
// Largest highlighted set the map draws; above it nothing highlights or dims.
export const MAX_WELLS_TO_HIGHLIGHT = 90000;

// Highlighted series redraw on these layers, pinned above every well layer, while
// the well layers dim.
export const WELL_LAYER_HIGHLIGHTED = "Wells Highlighted";
export const WELL_LAYER_POINT_HIGHLIGHTED = "Wells Vert Highlighted";
export const WELL_HIGHLIGHT_LAYERS = [
  WELL_LAYER_HIGHLIGHTED,
  WELL_LAYER_POINT_HIGHLIGHTED
] as const;
// 10 is the maximum of the line width input.
export const highlightedWellLineThickness = (thickness: number) =>
  thickness >= 7 ? 10 : thickness + 3;

/**
 * Values eva-map-svc writes into a well feature's `group` property as SHAPE
 * discriminators rather than group-by bin labels. A leg always carries "leg"
 * (with its parent's `Uwi`), and a stick always carries "stick" since
 * eva-map-svc#607. Before it, the txn tile path (filters ≥100k wells) wrote a
 * stick's bin label instead, which this set cannot recognise — such a stick
 * draws on WELL_LAYER and hovers like a bin.
 * A real bin label always arrives count-prefixed as "(N) Title", so it can
 * never collide with these two values. Deliberately NOT here: "inj" — client
 * side only, assigned onto ISelectedWell (see isSubordinateWell). See
 * src/components/map/CONTEXT.md Invariants and EVA-245.
 */
export const LEG_GROUP = "leg";
export const STICK_GROUP = "stick";
export const GROUP_SHAPE_DISCRIMINATORS: ReadonlySet<string> = new Set([
  LEG_GROUP,
  STICK_GROUP
]);

/**
 * The SOURCE ids that carry well geometry — for querySourceFeatures, which
 * takes a source id and does NOT apply layer filters.
 *
 * Only two, and deliberately not WELL_PAINT_LAYERS: horizontals and verticals
 * arrive as separate vector sources (hz.mvt against vert.mvt), each registered
 * under its layer id by addLayer's inline-source form, so a query against one
 * can never return the other. STICK_LAYER is absent because it declares
 * `source: WELL_LAYER` and has no source of its own — stick features already
 * come back from the wells source, exactly because layer filters do not apply
 * to this call.
 */
export const WELL_SOURCE_IDS = [WELL_LAYER, WELL_LAYER_POINT] as const;
export const FACILITY_PAINT_LAYERS = [FACILITY_LAYER] as const;
export const HZ_WELL_RASTER_LAYER = "Wells Hz Raster";
// Raster counterpart of the base well layer, drawn below HZ_RASTER_MVT_CROSSOVER_ZOOM
// exactly like HZ_WELL_RASTER_LAYER is for the interactive well layer (EVA-35).
export const BASE_WELL_RASTER_LAYER = "Base Well Raster";
// Legacy GeoServer range; the layer-tree slider falls back to it when the layer
// was added outside Map.tsx's addLayer and so has no store entry.
export const BASE_WELL_DEFAULT_ZOOM = { minzoom: 11, maxzoom: 22 } as const;
export const XDA_INTERCEPT_LAYER = "XDA Line";
export const XDA_LABEL = "XDA Label";
export const XDA_PROJECTION_LAYER = "XDA Projection";
export const XDA_WELL_INTERCEPT_POINT = "XDA Well Points";

export const dependantLayers = [
  `${BASE_WELL_LAYER}-lines`, // Dependant on BASE_WELL_LAYER
  BASE_WELL_RASTER_LAYER, // Dependant on BASE_WELL_LAYER
  SELECTED_WELL_POINT_LAYER, // Dependant on SELECTED_WELL_LAYER
  // SYNC_WELL_LAYER, // Dependant on SELECTED_WELL_LAYER
  SELECTED_SINGLE_WELL_LAYER, // Dependant on SELECTED_WELL_LAYER
  SELECTED_SINGLE_WELL_POINT_LAYER, // Dependant on SELECTED_WELL_LAYER
  WELL_LAYER_POINT, // Dependant on WELL_LAYER
  STICK_LAYER, // Dependant on WELL_LAYER
  TYPE_LOG_VERTICAL_LAYER // Dependant on TYPE_LOG_LAYER
];

// Mapbox Vector Tile Source Layers
export const WELL_SOURCE_LAYER = "eva-wells";
export const FACILITY_SOURCE_LAYER = "eva-facility";
export const RIG_FRAC_SOURCE_LAYER = "eva-rigfrac";

export const BUBBLE_MIN_ZOOM = 8;
export const COVERAGE_MIN_ZOOM = 8;
export const COMPLETION_MIN_ZOOM = 9;
export const XDA_PROJECTION_MIN_ZOOM = BUBBLE_MIN_ZOOM;
export const ZOOM_LEVEL_TO_ACTIVATE_SPIDERIFY = 11;

/**
 * Zoom level at which we switch from raster tiles (zoomed out) to MVT
 * vector tiles (zoomed in). Raster maxzoom = this value, MVT minzoom = this value.
 * Shared between useMapTileSourceManager (which builds the raster/MVT sources)
 * and mapboxHelper (which uses it to decide whether an empty rendered-feature
 * set means "genuinely nothing rendered" vs "raster is active at this zoom").
 */
export const HZ_RASTER_MVT_CROSSOVER_ZOOM = 8;

export enum FACILITY_TYPE_ENUM {
  // TODO: Add more facility types as needed
  GasPlant = "GasPlant"
}

/**
 * Supported file extensions for Geo Model uploads
 */
export enum GeoModelFileExtension {
  Tif = ".tif",
  Tiff = ".tiff",
  Z = ".z",
  Zmap = ".zmap",
  Prj = ".prj"
}

/** Raster file extensions that contain geo model data */
export const GEO_MODEL_RASTER_EXTENSIONS = [
  GeoModelFileExtension.Tif,
  GeoModelFileExtension.Tiff,
  GeoModelFileExtension.Z,
  GeoModelFileExtension.Zmap
] as const;

/** Projection file extension */
export const GEO_MODEL_PROJECTION_EXTENSION = GeoModelFileExtension.Prj;
